import {Component, OnInit} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {catchError, first, map, Observable, of, switchMap} from 'rxjs';
import {AuthenticationService} from '../../_auth/authentication.service';
import {UrlHelper} from '../../helpers/url-helper';
import {AppRoutes} from '../../enums/routes';
import {needsEntryAfterSignIn} from '../../_auth/context/context.models';

/**
 * Dedicated landing page for the external sign-in handoff (any provider: direct Google/Microsoft/Apple,
 * dev-login, …). The provider callback redirects here with a one-time `handoff` code (or an `error`); this page
 * exchanges it for the session and routes home — so the user sees a clean "signing in" loader instead of the
 * login form flashing + a refresh.
 *
 * It is also reached from the invitation landing page, in which case the URL carries `?invite=<code>` beside the
 * handoff or error. That code changes nothing about the exchange; it only decides where the failure branch's
 * back button leads (see {@link inviteCode} and the template).
 */
@Component({
  selector: 'app-signing-in',
  standalone: true,
  imports: [RouterLink],
  template: `
    <div class="fixed inset-0 -z-10 bg-gray-100"></div>
    <div class="flex min-h-full flex-col justify-center py-24 px-4 sm:px-6 lg:px-8">
      <div class="mx-auto w-full max-w-md">
        <div class="bg-white shadow-xl rounded-3xl border border-gray-100 p-8 text-center">
          <div class="icon-background h-20 w-20 rounded-full mx-auto grid place-content-center mb-6">
            <img alt="Logo" class="h-14 w-auto" src="./assets/images/icon_nobackground_md.png">
          </div>

          @if (!error) {
            <div class="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-theme-primary-600"></div>
            <h2 class="text-xl font-bold tracking-tight text-gray-900">Anmeldung läuft…</h2>
            <p class="mt-2 text-sm text-gray-500">Sie werden angemeldet.</p>
          } @else {
            <h2 class="text-xl font-bold tracking-tight text-gray-900">Anmeldung fehlgeschlagen</h2>
            <p class="mt-2 text-sm text-gray-500">{{ errorMessage }}</p>
            <!--
              WHERE THE WAY BACK LEADS (Bug #582). "Zurück zur Anmeldung" is the right offer for somebody who
              HAS an account and whose sign-in failed. It is a dead end for an invitee: they have no account to
              sign in to, the login form asks them for credentials that do not exist, and the invitation link
              they arrived on is no longer in any history they can reach. The invitation itself stays valid
              through every failure this page reports, so the useful next move is back to it — a different
              provider, the e-mail and password door, or simply reading the refusal again with the invitation in
              front of them.

              ONE class string, two destinations. The treatment is identical on purpose: this is the same button
              in the same place, and only where it leads changes. Holding the classes in a single @let is what
              makes that structural instead of a promise two copies have to keep.
            -->
            @let backButtonClass = 'mt-6 inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700 active:scale-95';
            @if (inviteCode) {
              <a [routerLink]="['/' + Routes.Invite]" [queryParams]="{code: inviteCode}" [class]="backButtonClass">
                Zurück zur Einladung
              </a>
            } @else {
              <a [routerLink]="['/' + Routes.Login]" [class]="backButtonClass">
                Zurück zur Anmeldung
              </a>
            }
          }
        </div>
      </div>
    </div>
  `
})
export class SigningInComponent implements OnInit {
  protected readonly Routes = AppRoutes;
  error = false;
  errorMessage = '';

  /**
   * The invitation this sign-in started from, or `null` when it did not start from one.
   *
   * Read from this page's own `?invite=<code>`, which the invitation landing page puts there: the provider door
   * folds it into the returnUrl it hands to `redeemInvitation(...)`, the native (e-mail + password) door
   * navigates here carrying it directly. It exists for exactly one purpose, the failure branch below.
   */
  protected inviteCode: string | null = null;

  // A one-time handoff must be exchanged exactly once. The component can be re-instantiated within a single app
  // load (e.g. a route re-init), which previously fired a second exchange of the now-consumed handoff: the 400
  // error handler then navigated home and aborted the first run's in-flight auto-switch. Tracking the handoff at
  // class scope makes the second run a no-op so the first run owns the exchange, the auto-switch and the landing.
  private static processedHandoff: string | null = null;

  constructor(private auth: AuthenticationService, private router: Router) {
  }

  ngOnInit(): void {
    // Read FIRST, ahead of every early return, because the failure branch is reachable from all of them and the
    // invitation context is what decides where that branch points (see the template's back button).
    //
    // WHY THE URL AND NOT `CookieService` / localStorage. The code is carried across the whole flow in the URL:
    // the provider door hands `…/signing-in?invite=<code>` to `redeemInvitation(...)` as the returnUrl, which
    // lives in SERVER-SIDE flow state (`ExternalSignInFlowController`'s `Flow.ReturnUrl`) for the duration of the
    // OIDC hop, and the callback APPENDS to it (`SocialSignInControllerBase.AppendQuery`, which respects an
    // existing query string: `url.Contains('?') ? '&' : '?'`). So `?invite=…` survives the round trip verbatim
    // and comes back as `?invite=…&error=…` or `?invite=…&handoff=…`.
    //
    // localStorage would survive the redirect too, and it is still the wrong store for this. It sits behind the
    // consent trap (`CookieService.setLocalStorage`), it would park a live bearer secret with no expiry and no
    // owner, and it would still be sitting there during the NEXT, unrelated sign-in. The code is already in the
    // URL the invitee is looking at, so carrying it in the URL widens nothing.
    //
    // `UrlHelper.QueryParams` is a Proxy over `window.location.search` and answers `null` for an absent key, so
    // the absent case needs no separate handling.
    this.inviteCode = UrlHelper.QueryParams.invite;

    const errorParam = UrlHelper.QueryParams.error;
    if (errorParam) {
      this.fail(SigningInComponent.messageFor(errorParam));
      return;
    }

    const handoff = UrlHelper.QueryParams.handoff;
    if (!handoff) {
      // Already signed in with nothing to exchange. Two very different arrivals land here, and they are told
      // apart by whether a context still has to be resolved rather than by a flag somebody has to pass:
      //
      //  * the NATIVE (e-mail + password) door, #279 — an XHR, so its session arrives in the response body and
      //    there is no handoff to redeem. It still comes here on purpose: this component owns the one decision
      //    about where a fresh external session goes (enter its single context silently, or ask which of
      //    several), and a second copy of that in the login form would be the copy that fell behind;
      //  * somebody navigating back to this loader, whose context was resolved on the first pass.
      //
      // The condition is "no context has been resolved yet", not "is this the native door": a session that
      // already holds one must go straight home, or a back-navigation would re-enter its context and mint an
      // equivalent second session for nothing.
      if (this.auth.isLoggedIn) {
        if (!this.auth.canSelectContext || !this.auth.isCompanyless) { window.open('/', '_self'); return; }

        this.resolveContext(null).pipe(first()).subscribe(diverted => {
          if (!diverted) window.open('/', '_self');
        });
        return;
      }
      this.fail('Es liegen keine Anmeldedaten vor.');
      return;
    }

    // A re-init for the same handoff: the first run already owns the exchange + landing — do nothing here, so we
    // don't re-exchange a consumed handoff (400) and navigate away mid context resolution.
    if (SigningInComponent.processedHandoff === handoff) return;
    SigningInComponent.processedHandoff = handoff;

    // A notice rides the SAME redirect as the handoff, so it is read once, up front, and held across the
    // exchange below. It is informational only: it must never decide whether the sign-in proceeds, and — unlike
    // the notice this channel used to carry — it must not hold the user on this loader either. It is handed to
    // the landing instead (see landingUrl), so they arrive in the app and read it there.
    const notice = UrlHelper.QueryParams.notice as string | null;

    // After the handoff exchange, context resolution (#279) decides where the session lands: one context is
    // entered silently, several send the user to the chooser, none just lands home. resolveContext never
    // rejects, so the landing always happens once the exchange succeeds.
    //
    // This used to run *behind* an `autoSwitchToCompany()` step that dropped an external accountant into their
    // last-used payroll company through the account switcher's `/auth/switch`. That endpoint is gone, and the
    // step is not missed: a payroll grant is an ordinary company-scoped `RoleGrant`, so it is already one of
    // the contexts listed here — a single one is auto-entered exactly as before, and an accountant holding
    // several is now ASKED which company rather than being dropped into whichever was used last. That is the
    // one deliberate behaviour change, and it is the same rule every other multi-context account follows.
    this.auth.completeSignIn(handoff).pipe(
      first(),
      switchMap(() => this.resolveContext(notice))
    ).subscribe({
      next: diverted => {
        // resolveContext already navigated to the chooser and carried the notice there. Landing here as well
        // would race that navigation and win, dropping the user straight past the choice being asked of them.
        if (diverted) return;
        window.open(SigningInComponent.landingUrl(notice), '_self');
      },
      error: () => {
        // A handoff is one-time + short-lived. If we're already signed in, this is a stale/duplicate exchange
        // (back button, refresh, double navigation) — go home silently. Otherwise send the user back to login.
        if (this.auth.isLoggedIn) { window.open('/', '_self'); return; }
        this.router.navigate(['/' + AppRoutes.Login]).then();
      }
    });
  }

  /**
   * Where a completed sign-in lands, carrying any notice with it.
   *
   * The landing is `window.open('/', '_self')` — a full document load — so component state does not survive it.
   * The notice therefore travels as a query parameter and is picked up on the other side by
   * `SignInNoticeComponent`, which strips it again. This mirrors how the migration notice already carries
   * `?claimed=success` across the same boundary, rather than inventing a third convention.
   */
  private static landingUrl(notice: string | null): string {
    return notice ? `/?notice=${encodeURIComponent(notice)}` : '/';
  }

  /**
   * Resolves where this freshly signed-in session should act (#279).
   *
   * Emits **true** when it has taken over the landing (the caller must then do nothing), **false** when the
   * ordinary landing should proceed.
   *
   * - **Exactly one context** ⇒ entered silently. `autoSelect` is the backend saying there is no choice to make,
   *   and honouring it is the point: one person at one shard is the common case in this product.
   * - **More than one** ⇒ the chooser. Before this, such a session simply landed with no context and no way to
   *   pick one — observed live after an invitation redeem left an account holding both `PlatformOwner` and a
   *   company `CompanyOwner`.
   * - **A 401** ⇒ true, and the failure is reported ON THIS PAGE. This is the one failure that is not about the
   *   follow-up question: the session was minted seconds ago, so a 401 means the session this browser holds is
   *   not usable at all. Degrading it to the landing sent such a session to `/`, where `AuthGuard` bounced it
   *   to `/login?returnUrl=%2F` — presenting a sign-in that fully succeeded as a login failure, with nothing on
   *   screen connecting the two. Saying so here is worse news but true news, and it is the only outcome that
   *   does not look like the login form simply reappeared. See {@link failCompleted}.
   * - **Anything else** (a password session with no `ext_id`, a 403 refusal, a network failure) ⇒ false. A
   *   sign-in that has already succeeded must never be turned into a dead end by a follow-up question that is
   *   merely unanswerable, so every *other* failure still degrades to the landing that existed before.
   */
  private resolveContext(notice: string | null): Observable<boolean> {
    // The endpoint reads the subject from the token's ext_id claim and refuses a password session outright, so
    // asking would be a guaranteed 403.
    if (!this.auth.canSelectContext) return of(false);

    return this.auth.availableContexts().pipe(
      switchMap(options => {
        const auto = options?.autoSelect;
        // Entering replaces the session; the caller then lands as usual, now with a context.
        // `needsEntryAfterSignIn`, not `canEnter`: the backend WOULD accept the platform shell now, but a
        // platform principal arrives here already holding that session, so entering it would mint an
        // equivalent second one for nothing. The chrome switcher offers it because by then the session
        // has been narrowed into a tenant.
        if (auto && needsEntryAfterSignIn(auto)) return this.auth.enterContext(auto).pipe(map(() => false));

        // More than one option is a question to ask. ZERO is a *different* question — "why does the access I
        // was just given open nothing?" — and the chooser is the only screen that answers it (#279, Defect A).
        // Landing such a session instead sent it to `/`, where `AuthGuard` parks a contextless external session
        // on its profile: no tiles, no message, no way forward. That is exactly what an owner invited to a
        // company whose first location does not exist yet saw, and that is the most likely onboarding order
        // there is.
        const count = options?.contexts?.length ?? 0;
        if (count === 0 || count > 1) {
          // A full document load, not a router navigation — same boundary every other landing in this flow
          // crosses. It also lets the notice ride along as `?notice=`: SignInNoticeComponent reads it while the
          // app bootstraps, so the aside appears beside the chooser instead of being lost with this component.
          window.open(SigningInComponent.chooserUrl(notice), '_self');
          return of(true);
        }

        return of(false);
      }),
      catchError(err => {
        if (err?.status === 401) {
          this.failCompleted();
          return of(true);
        }
        return of(false);
      })
    );
  }

  /**
   * Reports a sign-in that AUTHENTICATED but whose session cannot be used, without pretending it was a login
   * failure and without silently bouncing to the login form.
   *
   * Kept separate from {@link fail} because the two are different events and deserve different sentences: `fail`
   * covers a callback that never produced a session, this covers one that did and then could not be used.
   * Naming the difference is the whole point — the observed symptom of the old behaviour was "I signed in
   * successfully and landed on the login screen", which is unactionable.
   */
  private failCompleted(): void {
    this.fail('Die Anmeldung war erfolgreich, die Sitzung konnte aber nicht hergestellt werden. Bitte melden ' +
      'Sie sich erneut an.');
  }

  /** The chooser, carrying any notice so it is still announced on the page the user actually reaches. */
  private static chooserUrl(notice: string | null): string {
    const base = `/${AppRoutes.AuthenticationChooseContext}`;
    return notice ? `${base}?notice=${encodeURIComponent(notice)}` : base;
  }

  /**
   * What to tell somebody about a failed callback. The redeem flow propagates the backend's own error code
   * (`E_GRANT_…`) rather than one flat `redeem_failed`, because "wrong address" and "bad code" are different
   * problems with different next steps — the flat token made both read as "something broke".
   */
  private static messageFor(error: string): string {
    switch (error) {
      case 'E_GRANT_INVITE_EMAIL_MISMATCH':
        return 'Diese Einladung gilt für eine andere E-Mail-Adresse. Melden Sie sich mit dem Konto an, das zu ' +
          'dieser Adresse gehört. Die Einladung bleibt gültig.';
      case 'E_GRANT_INVITE_INVALID':
      case 'invite_invalid':
        return 'Diese Einladung ist abgelaufen, wurde bereits eingelöst oder zurückgezogen. Bitten Sie um eine ' +
          'neue Einladung.';
      case 'E_AUTH_ACCOUNT_DEACTIVATED':
        // Deliberately NOT folded into `no_account`. The account exists and a new invitation brings it back
        // with its history intact — "kein Zugang" would send its owner off to create a second one instead.
        return 'Dieses Konto wurde deaktiviert. Bitten Sie um eine neue Einladung. Damit wird es wieder ' +
          'aktiviert.';
      case 'no_account':
        return 'Für dieses Konto besteht kein Zugang. Lassen Sie sich einladen oder verknüpfen Sie Ihr ' +
          'bisheriges Login.';
      case 'no_access':
        return 'Dieses Konto hat noch keine Berechtigungen erhalten.';
      case 'cancelled':
        return 'Die Anmeldung wurde abgebrochen.';
      default:
        return 'Die Anmeldung konnte nicht abgeschlossen werden.';
    }
  }

  private fail(message: string) {
    this.error = true;
    this.errorMessage = message;
  }
}
